import { Component, forwardRef, Injector, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { AccessRestrictionReferenceDataFacade } from '@app/shared/common/facades/access-restriction-reference-data.facades';
import { EntityAccessRestrictionFacade } from '@app/shared/common/facades/entity-access-restriction.facade';
import { AppComponentBase } from '@shared/common/app-component-base';
import { IUserOrOrganizationUnitDto, IUserOrOrganizationUnitInput } from '@shared/service-proxies/common-interfaces';
import { forkJoin, Subscription, timer } from 'rxjs';
import { IItem } from '../../interfaces/item.interface';

@Component({
    selector: 'app-mazars-access-restriction-v2',
    templateUrl: './mazars-access-restriction-v2.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: MazarsAccessRestrictionV2Component,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MazarsAccessRestrictionV2Component),
            multi: true,
        },
    ],
})
export class MazarsAccessRestrictionV2Component extends AppComponentBase implements ControlValueAccessor, OnInit {
    @Input() isCreate: boolean;
    @Input() submitting: boolean;
    @Input() hasEditPermission: boolean;
    @Input() hasAdminPermission: boolean;
    @Input() customGrantsServiceProxy: any;
    @Input() accessRestrictionReferenceDataFacade: AccessRestrictionReferenceDataFacade;
    @Input() accessRestrictionFacade: EntityAccessRestrictionFacade;
    @Input() isAccessRestrictionActive: boolean;
    @Input() dataLoading: boolean;
    referenceDataLoading: boolean;
    form: FormGroup;
    isDisabled: boolean;
    subscriptions: Subscription[] = [];
    items: IItem<string>[] = [];
    accessRestrictionPossibleUsers: IUserOrOrganizationUnitDto[];

    constructor(injector: Injector, private fb: FormBuilder) {
        super(injector);

        this.createForm();

        this.subscriptions.push(
            this.form.valueChanges.subscribe((value) => {
                this.onChange(value);
                this.onTouched();
            })
        );
    }

    get value(): any {
        return this.form?.value;
    }

    set value(selectedAccessRestrictionPossibleUsers: any) {
        const value = {
            accessPermission: selectedAccessRestrictionPossibleUsers,
        };
        this.form.patchValue(value);
        this.form.updateValueAndValidity();

        if (this.isAccessRestrictionActive) {
            const unmappedSelectedAccessRestrictionPossibleUsers = this.unmapItemsToInput(selectedAccessRestrictionPossibleUsers);
            this.accessRestrictionFacade.setSelectedAccessRestrictionProvisionalData(unmappedSelectedAccessRestrictionPossibleUsers);
        }

        this.onChange(value);
        this.onTouched();
    }

    ngOnInit(): void {
        this.referenceDataLoading = true;
        this.subscriptions.push(
            this.accessRestrictionReferenceDataFacade.allReferenceDataAccessRestrictionUsers$.subscribe((result) => {
                if (!result) {
                    return;
                }
                this.accessRestrictionPossibleUsers = result.userOrOrganizations;
                this.mapAccessRestrictionPossibleUsers(this.accessRestrictionPossibleUsers);

                if (this.isCreate) {
                    this.isAccessRestrictionActive = result.isAccessRestrictionActive;
                    if (this.isAccessRestrictionActive) {
                        this.form.controls.accessPermission.setValidators([Validators.required]);
                        const initialValue = [`${this.appSession.userId}_User`];
                        this.value = initialValue;
                        this.subscriptions.push(
                            timer(0).subscribe(() => {
                                this.accessRestrictionFacade.markFormAsClean(true);
                            })
                        );
                    }
                    this.form.markAsPristine();
                }

                this.referenceDataLoading = false;
            })
        );

        this.accessRestrictionReferenceDataFacade.getAllReferenceDataAccessRestrictionUsers();
    }

    onAccessRestrictionChange(accessRestrictionUsersOrOrganizationUnitsIds) {
        if (this.isAccessRestrictionActive) {
            forkJoin({
                sourceOne: this.customGrantsServiceProxy.checkCurrentUserIsInList(this.unmapItemsToInput(this.form.value.accessPermission)),
                sourceTwo: this.customGrantsServiceProxy.checkCurrentUserIsInList(this.unmapItemsToInput(accessRestrictionUsersOrOrganizationUnitsIds)),
            }).subscribe((result) => {
                if (result.sourceOne === true && result.sourceTwo === false && !this.hasAdminPermission) {
                    this.message.warn(this.l('PermissionAccessRestriction_DropdownWarning'));
                }
            });

            this.accessRestrictionFacade.setSelectedAccessRestrictionProvisionalData(this.unmapSelectedItems(accessRestrictionUsersOrOrganizationUnitsIds));
        }
    }

    onChange: any = () => {
        // This is intentional
    };

    onTouched: any = () => {
        // This is intentional
    };

    writeValue(obj: any): void {
        if (obj) {
            this.value = obj;
        } else {
            this.form.reset();
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (this.isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    validate(_: FormControl) {
        return this.form.valid ? null : { accessRestriction: { valid: false } };
    }

    private createForm() {
        this.form = this.fb.group({
            accessPermission: [{ value: [], disabled: !this.hasEditPermission }],
        });
    }

    private mapAccessRestrictionPossibleUsers(accessRestrictionPossibleUsers: IUserOrOrganizationUnitDto[]) {
        this.items = accessRestrictionPossibleUsers
            ? accessRestrictionPossibleUsers.map(
                  (userOrOganizationUnit: IUserOrOrganizationUnitDto) =>
                      ({
                          id: `${userOrOganizationUnit.userOrOrganizationId}_${userOrOganizationUnit.type}`,
                          text: userOrOganizationUnit.displayName,
                          source: userOrOganizationUnit.type,
                      } as IItem<string>)
              )
            : [];
    }

    private unmapSelectedItems(selectedItems) {
        return selectedItems
            ? selectedItems.map((i) => ({ type: i.split('_')[1], userOrOrganizationId: i.split('_')[0] } as IUserOrOrganizationUnitDto))
            : null;
    }

    private unmapItemsToInput(selectedItems) {
        return selectedItems
            ? selectedItems.map((i) => ({ type: i.split('_')[1], userOrOrganizationId: i.split('_')[0] } as IUserOrOrganizationUnitInput))
            : null;
    }
}
